/****************************************************
 * Module Name:  mixDiamondViewStockBarChart.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import React from "react";

import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Container,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import MixBucketProxy from "../../utility/proxy/manager/mixProxy/mixBucketProxy";
import Select from "react-select";
import MixShapeProxy from "../../utility/proxy/manager/mixProxy/mixShapeProxy";
import MixSizeProxy from "../../utility/proxy/manager/mixProxy/mixSizeProxy";
import MixColorProxy from "../../utility/proxy/manager/mixProxy/mixColorProxy";
import MixClarityProxy from "../../utility/proxy/manager/mixProxy/mixClarityProxy";
import Constants from "../../utility/constants";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";

import { LoadingButton } from "@mui/lab";

import Utility from "../../utility/utility";
import {
  Bar,
  BarChart,
  Legend,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip as BarTooltip,
  CartesianGrid,
} from "recharts";
import { DiBitbucket } from "react-icons/di";
import { useState } from "react";
import { useEffect } from "react";
import CommonSnackbar from "../common/commonSnackBar";
import MixBucketModal from "./mixBucketModal";
import StoneTypeProxy from "../../utility/proxy/manager/stoneTypeProxy";

let useColor = Utility.GetRandomColorForBarChart();

const MixDiamondViewStockBarChart = ({
  setDataOfMaster,
  searchCriteria,
  masterLoader,
  apiCrashMasterError,
  setSearchCriteria,
  click,
}) => {
  const [data, setData] = useState([]);
  const [shape, setShape] = useState([]);
  const [shapeOptions, setShapeOptions] = useState([]);
  const [color, setColor] = useState([]);
  const [colorOptions, setColorOptions] = useState([]);
  const [size, setSize] = useState([]);
  const [sizeOptions, setSizeOptions] = useState([]);
  const [clarity, setClarity] = useState([]);
  const [clarityOptions, setClarityOptions] = useState([]);
  const [stoneType, setStoneType] = useState([]);
  const [stoneTypeOptions, setStoneTypeOptions] = useState([]);
  const [showBucketLoadSpinner, setShowBucketLoadSpinner] = useState(false);
  const [errorSnackbar, setErrorSnackbar] = useState(false);
  const [show, setShow] = useState(false);

  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    getAllShapeData();
    getAllSizeData();
    getAllColorData();
    getAllClarityData();
    getAllStoneTypeData();
  }, []);

  const getAllShapeData = () => {
    MixShapeProxy.GetAllShapeMini((response) => {
      setAllShapeData(response);
    });
  };

  const getAllSizeData = () => {
    MixSizeProxy.GetAllSizeMini((response) => {
      setAllSizeData(response);
    });
  };

  const getAllColorData = () => {
    MixColorProxy.GetAllColorMini((response) => {
      setAllColorData(response);
    });
  };

  const getAllClarityData = () => {
    MixClarityProxy.GetAllClarityMini((response) => {
      setAllClarityData(response);
    });
  };

  const getAllStoneTypeData = () => {
    StoneTypeProxy.GetAllStoneType((response) => {
      setAllStoneTypeData(response);
    });
  };

  const setAllShapeData = (response) => {
    let optionsArray = [];
    optionsArray.push({ value: "*", label: "-- ANY --" });
    response.map((data) => {
      let option = {
        value: data.id,
        label: data.id + " | " + data.name,
      };
      optionsArray.push(option);
      return true;
    });
    setShapeOptions(optionsArray);
    setShape(optionsArray.length > 0 ? optionsArray[0] : shape);
  };

  const setAllSizeData = (response) => {
    let optionsArray = [];
    optionsArray.push({ value: "*", label: "-- ANY --" });
    response.map((data) => {
      let option = {
        value: data.id,
        label: data.name,
      };
      optionsArray.push(option);
      return true;
    });
    setSizeOptions(optionsArray);
    setSize(optionsArray.length > 0 ? optionsArray[0] : size);
  };

  const setAllColorData = (response) => {
    let optionsArray = [];
    optionsArray.push({ value: "*", label: "-- ANY --" });
    response.map((data) => {
      let option = {
        value: data.id,
        label: data.name,
      };
      optionsArray.push(option);
      return true;
    });
    setColorOptions(optionsArray);
    setColor(optionsArray.length > 0 ? optionsArray[0] : color);
  };

  const setAllClarityData = (response) => {
    let optionsArray = [];
    optionsArray.push({ value: "*", label: "-- ANY --" });
    response.map((data) => {
      let option = {
        value: data.id,
        label: data.name,
      };
      optionsArray.push(option);
      return true;
    });
    setClarityOptions(optionsArray);
    setClarity(
      optionsArray.length > 0 ? optionsArray[0] : clarity
    );
  };

  const setAllStoneTypeData = (response) => {
    let optionsArray = [];
    optionsArray.push({ value: "*", label: "-- ANY --" });
    response.map((data) => {
      let option = {
        value: data.name,
        label: data.fullname,
      };
      optionsArray.push(option);
      return true;
    });
    setStoneTypeOptions(optionsArray);
    setStoneType(
      optionsArray.length > 0 ? optionsArray[0] : stoneType
    );
  };

  const fullOptionToValue = (selectedData, optionArr) => {
    if (selectedData.value === "*") {
      return [];
    }
    return Utility.GetValueArrayWithStarReplace(
      selectedData,
      Utility.GetValueArrayWithoutStar(optionArr)
    );
  };

  const handleSearch = () => {
    setShowBucketLoadSpinner(true);
    if (masterLoader) {
      masterLoader(true);
    }

    let data = {
      shapeid: fullOptionToValue(shape, shapeOptions),
      sizeid: fullOptionToValue(size, sizeOptions),
      colorid: fullOptionToValue(color, colorOptions),
      clarityid: fullOptionToValue(clarity, clarityOptions),
      stonetypename: fullOptionToValue(stoneType, stoneTypeOptions)
    };
    MixBucketProxy.GetAllStockReport(data, (responseData) => {
      if (responseData) {
        setData(responseData);
        setShowBucketLoadSpinner(false);
        if (setDataOfMaster) {
          setDataOfMaster(responseData);
        }
        if (masterLoader) {
          masterLoader(false);
        }
        if (setSearchCriteria) {
          setSearchCriteria(false);
        }
      } else {
        setErrorSnackbar(true);
        setShowBucketLoadSpinner(false);
        if (masterLoader) {
          masterLoader(false);
        }
        if (apiCrashMasterError) {
          apiCrashMasterError(true);
        }
      }
    });
  };

  useEffect(() => {
    handleSearch();
  }, []);

  useEffect(() => {
    if (searchCriteria) {
      handleSearch();
    }
  }, [searchCriteria]);

  const handleModalOpen = (data) => {
    if (click) {
      setShow(true);
    }
  };

  return (
    <Container maxWidth="xxl" disableGutters>
      <CommonSnackbar
        show={errorSnackbar}
        message={Constants.APiFailErrorMessage}
        variant={"error"}
        autoHide={Constants.SnackBarCloseTimer}
        title={"Error"}
        handleClose={() => setErrorSnackbar(false)}
      />
      <MixBucketModal
        show={show}
        barSourceData={data}
        handleClose={() => setShow(false)}
      />
      <Paper elevation={matches ? 3 : 0}>
        <Box
          sx={{
            display: "flex",
            width: "100%",
            mb: 3,
            mt: 2,
            gap: !matches ? "15px" : "5px",
            flexWrap: "wrap",
            p: "15px",
            justifyContent: matches ? "center" : undefined,
            alignItems: matches ? "center" : undefined,
          }}
        >
          <Box
            sx={{
              display: "flex",
              minWidth: !matches ? "250px" : "100%",
              maxWidth: !matches ? "50%" : "100%",
              gap: "40px",
            }}
          >
            <Box sx={{ alignSelf: "center", mt: 1, minWidth: "40px" }}>
              <Typography>Stonetype</Typography>
            </Box>
            <Box
              sx={{
                alignSelf: "center",
                width: "100%",
                mt: 1,
              }}
            >
              <Select menuPlacement="auto"
                blurInputOnSelect={false}
                menuPortalTarget={document.body}
                styles={Constants.Styles}
                isMulti
                closeMenuOnSelect={false}
                getOptionLabel={(option) => option.label}
                getOptionValue={(option) => option.value}
                options={stoneTypeOptions}
                value={stoneType}
                onChange={(e) => setStoneType(e)}
              />
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              minWidth: !matches ? "250px" : "100%",
              maxWidth: !matches ? "50%" : "100%",
              gap: "20px",
            }}
          >
            <Box sx={{ alignSelf: "center", mt: 1, minWidth: "40px" }}>
              <Typography>Shape</Typography>
            </Box>
            <Box
              sx={{
                alignSelf: "center",
                width: "100%",
                mt: 1,
              }}
            >
              <Select menuPlacement="auto"
                blurInputOnSelect={false}
                menuPortalTarget={document.body}
                styles={Constants.Styles}
                isMulti
                closeMenuOnSelect={false}
                getOptionLabel={(option) => option.label}
                getOptionValue={(option) => option.value}
                options={shapeOptions}
                value={shape}
                onChange={(e) => setShape(e)}
              />
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              minWidth: !matches ? "250px" : "100%",
              maxWidth: !matches ? "50%" : "100%",
              gap: "20px",
            }}
          >
            <Box sx={{ alignSelf: "center", mt: 1, minWidth: "40px" }}>
              <Typography>Size</Typography>
            </Box>
            <Box
              sx={{
                alignSelf: "center",
                width: "100%",
                mt: 1,
              }}
            >
              <Select menuPlacement="auto"
                blurInputOnSelect={false}
                menuPortalTarget={document.body}
                styles={Constants.Styles}
                isMulti
                closeMenuOnSelect={false}
                getOptionLabel={(option) => option.label}
                getOptionValue={(option) => option.value}
                options={sizeOptions}
                value={size}
                onChange={(e) => setSize(e)}
              />
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              minWidth: !matches ? "250px" : "100%",
              maxWidth: !matches ? "50%" : "100%",
              gap: "20px",
            }}
          >
            <Box sx={{ alignSelf: "center", mt: 1, minWidth: "40px" }}>
              <Typography>Color</Typography>
            </Box>
            <Box
              sx={{
                alignSelf: "center",
                width: "100%",
                mt: 1,
              }}
            >
              <Select menuPlacement="auto"
                blurInputOnSelect={false}
                menuPortalTarget={document.body}
                styles={Constants.Styles}
                isMulti
                closeMenuOnSelect={false}
                getOptionLabel={(option) => option.label}
                getOptionValue={(option) => option.value}
                options={colorOptions}
                value={color}
                onChange={(e) => setColor(e)}
              />
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              minWidth: !matches ? "250px" : "100%",
              maxWidth: !matches ? "50%" : "100%",
              gap: "20px",
            }}
          >
            <Box sx={{ alignSelf: "center", mt: 1, minWidth: "40px" }}>
              <Typography>Clarity</Typography>
            </Box>
            <Box
              sx={{
                alignSelf: "center",
                width: "100%",
                mt: 1,
              }}
            >
              <Select menuPlacement="auto"
                blurInputOnSelect={false}
                menuPortalTarget={document.body}
                styles={Constants.Styles}
                isMulti
                closeMenuOnSelect={false}
                getOptionLabel={(option) => option.label}
                getOptionValue={(option) => option.value}
                options={clarityOptions}
                value={clarity}
                onChange={(e) => setClarity(e)}
              />
            </Box>
          </Box>

          <Box sx={{ mt: 1 }}>
            <LoadingButton
              loading={showBucketLoadSpinner}
              variant="contained"
              loadingPosition="start"
              onClick={() => handleSearch()}
              startIcon={<SearchOutlinedIcon size="large" />}
              disabled={
                shapeOptions.length === 0 ||
                clarityOptions.length === 0 ||
                colorOptions.length === 0 ||
                sizeOptions.length === 0 ||
                stoneTypeOptions.length === 0
              }
            >
              Search
            </LoadingButton>
          </Box>
        </Box>
        <Card>
          <CardHeader
            avatar={<DiBitbucket size={25} color={useColor} />}
            title={
              <Typography
                component="span"
                fontWeight={Constants.defaultFontWeight}
                fontSize={"1.25rem"}
              >
                Mix Stock by Bucket
              </Typography>
            }
            subheader="Mix diamond stock for each Bucket"
          />
          <CardContent>
            <Box>
              <ResponsiveContainer height={600}>
                <BarChart
                  data={data}
                  onClick={(data) => handleModalOpen(data)}
                  margin={{
                    top: 20,
                    right: 20,
                    bottom: 20,
                    left: 20,
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey={(value) =>
                      value.stonetypefullname +
                      " | " +
                      value.shape +
                      " | " +
                      value.size +
                      " | " +
                      value.color +
                      " | " +
                      value.clarity
                    }
                    hide={matches}
                  />
                  <YAxis hide={matches} />
                  <BarTooltip />
                  <Legend formatter={() => "QUANTITY(CTS)"} />
                  <Bar
                    dataKey="qty"
                    name="Quantity"
                    fill={useColor}
                    unit={"cts"}
                    stroke="#000000"
                    strokeWidth={1}
                    label={{ position: "top" }}
                  />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>
      </Paper>
    </Container>
  );
};

export default MixDiamondViewStockBarChart;
